if (!customElements.get('ceramic-pan-bundle')) {
  customElements.define(
    'ceramic-pan-bundle',
    class CeramicPanBundle extends HTMLElement {
      connectedCallback() {
        this.records = Array.from(this.querySelectorAll('[data-cfp-variant]')).map((element) => ({
          mode: element.dataset.mode,
          size: element.dataset.size,
          sizeLabel: element.dataset.sizeLabel,
          color: element.dataset.color,
          id: Number(element.dataset.id),
          price: Number(element.dataset.price) || 0,
          compare: Number(element.dataset.compare) || 0,
          available: element.dataset.available === 'true',
        }));
        this.modeInputs = Array.from(this.querySelectorAll('[data-cfp-mode]'));
        this.sizeInputs = Array.from(this.querySelectorAll('[data-cfp-size]'));
        this.colorInputs = Array.from(this.querySelectorAll('[data-cfp-color]'));
        this.panels = Array.from(this.querySelectorAll('[data-cfp-panel]'));
        this.quantity = this.querySelector('[data-cfp-quantity]');
        this.lidToggle = this.querySelector('[data-cfp-lid]');
        this.lidQuantity = this.querySelector('[data-cfp-lid-quantity]');
        this.lidCard = this.querySelector('[data-cfp-lid-card]');
        this.selected = this.querySelector('[data-cfp-selected]');
        this.total = this.querySelector('[data-cfp-total]');
        this.compare = this.querySelector('[data-cfp-compare]');
        this.saving = this.querySelector('[data-cfp-saving]');
        this.submitButton = this.querySelector('[data-cfp-submit]');
        this.status = this.querySelector('[data-cfp-status]');
        this.currency = this.dataset.currency || 'JPY';

        [...this.modeInputs, ...this.sizeInputs, ...this.colorInputs].forEach((input) => {
          input.addEventListener('change', () => this.update());
        });
        this.quantity?.addEventListener('input', () => this.update());
        this.lidToggle?.addEventListener('change', () => {
          if (this.lidQuantity) this.lidQuantity.disabled = !this.lidToggle.checked;
          this.lidCard?.classList.toggle('is-selected', this.lidToggle.checked);
          this.update();
        });
        this.lidQuantity?.addEventListener('input', () => this.update());
        this.submitButton?.addEventListener('click', () => this.addToCart());
        this.update();
      }

      getMode() {
        return this.modeInputs.find((input) => input.checked)?.value || 'single';
      }

      getChecked(inputs, mode) {
        return inputs.find((input) => input.dataset.mode === mode && input.checked)?.value || '';
      }

      getSelection() {
        const mode = this.getMode();
        const size = mode === 'set3' ? '20-24-lid' : this.getChecked(this.sizeInputs, mode);
        const color = this.getChecked(this.colorInputs, mode);
        return this.records.find((record) => record.mode === mode && record.size === size && record.color === color) || null;
      }

      formatMoney(cents) {
        try {
          return new Intl.NumberFormat('ja-JP', {
            style: 'currency',
            currency: this.currency,
            maximumFractionDigits: this.currency === 'JPY' ? 0 : 2,
          }).format(cents / 100);
        } catch (error) {
          return `${Math.round(cents / 100).toLocaleString('ja-JP')}円`;
        }
      }

      updateAvailability(mode) {
        const size = mode === 'set3' ? '20-24-lid' : this.getChecked(this.sizeInputs, mode);

        this.sizeInputs.forEach((input) => {
          if (input.dataset.mode !== mode) return;
          const hasStock = this.records.some((record) => record.mode === mode && record.size === input.value && record.available);
          input.closest('.cfp-size-card')?.classList.toggle('is-soldout', !hasStock);
        });

        this.colorInputs.forEach((input) => {
          if (input.dataset.mode !== mode) return;
          const record = this.records.find((item) => item.mode === mode && item.size === size && item.color === input.value);
          const card = input.closest('.cfp-color-card');
          card?.classList.toggle('is-soldout', !record?.available);
          card?.setAttribute('aria-disabled', record?.available ? 'false' : 'true');
          const stock = card?.querySelector('[data-cfp-color-stock]');
          if (stock) stock.textContent = record?.available ? '' : '再入荷待ち';
        });
      }

      update() {
        const mode = this.getMode();
        this.panels.forEach((panel) => {
          panel.hidden = panel.dataset.cfpPanel !== mode;
        });
        this.modeInputs.forEach((input) => {
          input.closest('.cfp-mode-card')?.classList.toggle('is-selected', input.checked);
        });
        this.sizeInputs.forEach((input) => {
          input.closest('.cfp-size-card')?.classList.toggle('is-selected', input.checked);
        });
        this.colorInputs.forEach((input) => {
          input.closest('.cfp-color-card')?.classList.toggle('is-selected', input.checked);
        });
        this.updateAvailability(mode);

        const record = this.getSelection();
        const quantity = Math.max(1, Number(this.quantity?.value) || 1);
        const colorLabel = record?.color === 'white' ? 'ホワイト' : 'ブラック';
        let label = record ? `${record.sizeLabel} / ${colorLabel}` : '内容を選択';
        let total = record ? record.price * quantity : 0;

        if (mode === 'single' && this.lidToggle?.checked && !this.lidToggle.disabled) {
          const lidQuantity = Math.max(1, Number(this.lidQuantity?.value) || 1);
          total += (Number(this.lidToggle.dataset.price) || 0) * lidQuantity;
          label += ' ＋ 兼用蓋';
        }

        if (this.selected) this.selected.textContent = label;
        if (this.total) this.total.textContent = record ? this.formatMoney(total) : '—';

        const hasDiscount = !!record && record.compare > record.price;
        if (this.compare) this.compare.textContent = hasDiscount ? `通常 ${this.formatMoney(record.compare * quantity)}` : '';
        if (this.saving) this.saving.textContent = hasDiscount ? `${this.formatMoney((record.compare - record.price) * quantity)} お得` : '';

        const unavailable = !record || !record.available;
        if (this.submitButton) {
          this.submitButton.disabled = unavailable;
          this.submitButton.textContent = unavailable ? '選択中の商品は再入荷待ちです' : '選んだ内容をカートに追加';
        }
        this.setStatus(unavailable && record ? '別のサイズまたはカラーをお選びください。' : '');
      }

      getItems() {
        const record = this.getSelection();
        if (!record?.available) return [];
        const items = [{ id: record.id, quantity: Math.max(1, Number(this.quantity?.value) || 1) }];

        if (record.mode === 'single' && this.lidToggle?.checked && !this.lidToggle.disabled) {
          items.push({
            id: Number(this.lidToggle.value),
            quantity: Math.max(1, Number(this.lidQuantity?.value) || 1),
          });
        }
        return items;
      }

      setStatus(message, isError = false) {
        if (!this.status) return;
        this.status.textContent = message;
        this.status.classList.toggle('is-error', isError);
      }

      async addToCart() {
        const items = this.getItems();
        if (!items.length) {
          this.setStatus('購入可能なサイズとカラーを選択してください。', true);
          return;
        }

        const cartNotification = document.querySelector('cart-notification');
        const sections = cartNotification?.getSectionsToRender().map((section) => section.id) || [];
        const cartAddUrl = window.routes?.cart_add_url || '/cart/add.js';

        this.submitButton.disabled = true;
        this.submitButton.classList.add('loading');
        this.setStatus('カートに追加しています…');

        try {
          const response = await fetch(cartAddUrl, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error(result.description || result.message || 'カートに追加できませんでした。');

          this.setStatus(`${items.length}点をカートに追加しました。`);
          if (cartNotification && result.sections) {
            cartNotification.setActiveElement?.(this.submitButton);
            cartNotification.renderContents(result);
          } else {
            window.location.href = window.routes?.cart_url || '/cart';
          }
        } catch (error) {
          this.setStatus(error.message || 'カートに追加できませんでした。時間をおいて再度お試しください。', true);
        } finally {
          this.submitButton.classList.remove('loading');
          this.submitButton.disabled = false;
          this.update();
        }
      }
    }
  );
}
